import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Lock, Shield, FileCheck, Activity, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // MFA states
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (mfaRequired) {
      if (mfaCode.length !== 6 && mfaCode.length !== 14) {
        toast.error('Please enter a valid MFA code or recovery code');
        return;
      }
      
      setLoading(true);
      try {
        const result = await login(email, password, mfaCode);
        if (result && !result.mfa_required) {
          toast.success('Welcome back!');
          navigate('/home');
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Invalid MFA code');
      } finally {
        setLoading(false);
      }
      return;
    }
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      const result = await login(email, password);
      
      if (result?.mfa_required) {
        setMfaRequired(true);
        toast.info('Please enter your MFA code');
      } else {
        toast.success('Welcome back!');
        navigate('/home');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const resetMFA = () => {
    setMfaRequired(false);
    setMfaCode('');
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="login-page">
      {/* Left Panel - Brand Section */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden">
        {/* Soft gradient background */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(145deg, var(--color-surface) 0%, var(--color-primary-light) 100%)'
          }}
        />
        
        {/* Subtle dot pattern */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, var(--color-primary) 1px, transparent 0)`,
            backgroundSize: '24px 24px'
          }}
        />
        
        {/* Content Container */}
        <div className="relative z-10 flex flex-col w-full h-full px-12 py-12">
          {/* Logo with company name */}
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_4cf3c2ed-55c0-4e2e-88f8-3504a875a61f/artifacts/0bt8uv0u_faviconM.png" 
              alt="Meridian" 
              className="h-12 w-12 object-contain"
              data-testid="brand-logo"
            />
            <div className="flex flex-col">
              <span className="text-xl font-bold tracking-tight" style={{ color: 'var(--color-primary-dark)' }}>MERIDIAN</span>
              <span className="text-xl font-bold tracking-tight" style={{ color: 'var(--color-primary-dark)' }}>DRAWBACK</span>
              <span className="text-xs font-medium tracking-wider text-gray-400">UK</span>
            </div>
          </div>
          
          {/* Value Proposition - Centered vertically */}
          <div className="flex-1 flex items-center">
            <div className="max-w-sm">
              <h1 
                className="text-[28px] font-bold leading-tight mb-8"
                style={{ color: 'var(--color-primary)' }}
              >
                Compliance-grade duty & VAT recovery.
              </h1>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div 
                    className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center mt-0.5"
                    style={{ backgroundColor: 'rgba(0, 82, 204, 0.08)' }}
                  >
                    <FileCheck className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-[15px]">Automated evidence packs</p>
                    <p className="text-sm text-gray-500 mt-1">HMRC-ready claims with full audit trail</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div 
                    className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center mt-0.5"
                    style={{ backgroundColor: 'rgba(0, 82, 204, 0.08)' }}
                  >
                    <Shield className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-[15px]">Workspace isolation</p>
                    <p className="text-sm text-gray-500 mt-1">Tenant-isolated data with audit trails by design</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div 
                    className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center mt-0.5"
                    style={{ backgroundColor: 'rgba(0, 82, 204, 0.08)' }}
                  >
                    <Activity className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-[15px]">Real-time sync health</p>
                    <p className="text-sm text-gray-500 mt-1">Exception handling and connector monitoring</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <div className="pt-8">
            <p className="text-xs text-gray-400">
              © {new Date().getFullYear()} Meridian Drawback UK. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-[55%] flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-[400px]">
          {/* Mobile Logo */}
          <div className="flex justify-center mb-8 lg:hidden">
            <div className="flex items-center gap-2.5">
              <img 
                src="https://customer-assets.emergentagent.com/job_4cf3c2ed-55c0-4e2e-88f8-3504a875a61f/artifacts/0bt8uv0u_faviconM.png" 
                alt="Meridian" 
                className="h-10 w-10 object-contain"
              />
              <div className="flex flex-col">
                <span className="text-lg font-bold tracking-tight" style={{ color: 'var(--color-primary-dark)' }}>MERIDIAN</span>
                <span className="text-lg font-bold tracking-tight" style={{ color: 'var(--color-primary-dark)' }}>DRAWBACK</span>
              </div>
            </div>
          </div>

          {/* Login Card */}
          <div 
            className="bg-white rounded-2xl p-8"
            style={{
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 8px 32px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.06)'
            }}
          >
            {/* Card Header */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {mfaRequired ? 'Two-factor authentication' : 'Welcome back'}
              </h2>
              <p className="text-gray-500 mt-1.5 text-sm">
                {mfaRequired 
                  ? 'Enter the code from your authenticator app' 
                  : 'Sign in to your Meridian account'}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              {!mfaRequired ? (
                <div className="space-y-5">
                  {/* Email Field */}
                  <div>
                    <Label 
                      htmlFor="email" 
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Email address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="login-email"
                      autoComplete="email"
                      className="px-3.5 text-sm"
                    />
                  </div>

                  {/* Password Field */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label 
                        htmlFor="password" 
                        className="text-sm font-medium text-gray-700"
                      >
                        Password
                      </Label>
                      <Link 
                        to="/forgot-password" 
                        className="text-sm font-medium hover:underline transition-colors"
                        style={{ color: 'var(--color-primary)' }}
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        data-testid="login-password"
                        autoComplete="current-password"
                        className="px-3.5 pr-11 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        tabIndex={-1}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                      </button>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-1">
                    <Button 
                      type="submit" 
                      className="w-full font-semibold"
                      style={{ 
                        backgroundColor: 'var(--color-primary)',
                        color: 'white'
                      }}
                      disabled={loading}
                      data-testid="login-submit"
                    >
                      {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Sign in
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* MFA Code Input */}
                  <div>
                    <Label 
                      htmlFor="mfaCode" 
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Authentication code
                    </Label>
                    <Input
                      id="mfaCode"
                      type="text"
                      placeholder="000000"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      data-testid="mfa-code-input"
                      autoComplete="one-time-code"
                      className="px-3.5 text-xl text-center tracking-[0.4em] font-mono placeholder:tracking-[0.4em]"
                      maxLength={6}
                      autoFocus
                    />
                    <p className="text-xs text-gray-500 text-center mt-2">
                      Enter the 6-digit code from your authenticator app
                    </p>
                  </div>

                  {/* MFA Submit Button */}
                  <div className="pt-1">
                    <Button 
                      type="submit" 
                      className="w-full font-semibold"
                      style={{ 
                        backgroundColor: 'var(--color-primary)',
                        color: 'white'
                      }}
                      disabled={loading || mfaCode.length < 6}
                      data-testid="mfa-submit"
                    >
                      {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      <KeyRound className="w-4 h-4 mr-2" />
                      Verify code
                    </Button>
                  </div>

                  {/* Back Button */}
                  <button
                    type="button"
                    onClick={resetMFA}
                    className="w-full flex items-center justify-center gap-1.5 h-10 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                    data-testid="mfa-back-btn"
                  >
                    ← Back to login
                  </button>
                </div>
              )}
            </form>

            {/* Trust Indicators */}
            {!mfaRequired && (
              <div className="mt-5 pt-5 border-t border-gray-100">
                <div className="flex items-center justify-center gap-3 text-xs text-gray-400">
                  <div className="flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    <span>Secure sign-in</span>
                  </div>
                  <span className="text-gray-200">•</span>
                  <span>Encrypted credentials</span>
                  <span className="text-gray-200">•</span>
                  <span>Tenant-isolated</span>
                </div>
              </div>
            )}
          </div>

          {/* Early Access CTA — Iter 219 (replaces the old self-serve trial banner) */}
          {!mfaRequired && (
            <div
              className="mt-4 rounded-md border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50/60 px-5 py-4 flex items-center justify-between gap-3"
              data-testid="early-access-cta-panel"
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900" data-testid="early-access-cta-heading">
                  New to Meridian?
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Meridian Compliance is in early access · Pricing available on request
                </p>
              </div>
              <Link
                to="/request-access"
                className="flex-shrink-0 inline-flex items-center gap-1 px-3.5 py-2 rounded-lg text-sm font-semibold bg-white border border-blue-200 text-primary hover:bg-blue-50 hover:border-blue-300 transition-all duration-150"
                data-testid="early-access-cta-btn"
              >
                Request access →
              </Link>
            </div>
          )}

          {/* Support Link */}
          <div className="mt-5 text-center">
            <p className="text-sm text-gray-500">
              Need help?{' '}
              <a 
                href="mailto:info@meridiandrawback.co.uk" 
                className="font-medium hover:underline transition-colors"
                style={{ color: 'var(--color-primary)' }}
              >
                Contact support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
